// Local chat state (bubbles + chat log) written into the UI bridge.
import chips from '../../../../content/lang/chips.json';

/** UI emote names (B2's EmoteWheel) <-> wire EmoteId (43 §3.6: 1 wave, 2 hail, 3 eh-hen). */
export const EMOTE_WIRE = { wave: 1, hail: 2, ehen: 3 };
export const EMOTE_NAME = { 1: 'wave', 2: 'hail', 3: 'ehen' };
const CHIP_TEXT = new Map(chips.map((c) => [c.id, c.text]));
export const chipText = (id) => CHIP_TEXT.get(id) ?? id;

const SAY_TTL_MS = 6000;
const EMOTE_TTL_MS = 2000;
const LOG_MAX = 100;
// Head of a standing character above its feet, in world px (doll frame: ~86 px).
const HEAD_PX = 92;

/**
 * Keeps live bubbles anchored to characters and mirrors them into
 * `bridge.bubbles` ({id, text, x, y, at, kind, emote}) each frame.
 */
export class ChatState {
    constructor(bridge, now = () => Date.now()) {
        this.bridge = bridge;
        this.now = now;
        this.live = [];
        this.nextId = 1;
        this.wroteEmpty = true;
    }
    log(entry) {
        const list = this.bridge.chatLog.value;
        const next = [...list, { id: this.nextId++, at: this.now(), ...entry }];
        this.bridge.chatLog.value = next.length > LOG_MAX ? next.slice(next.length - LOG_MAX) : next;
    }
    /** A say/chip bubble or an emote icon over character `ch` (an engine Character, or null for log only). */
    bubble(ch, kind, text, emote) {
        if (!ch) return;
        const b = { id: this.nextId++, ch, kind, at: this.now() };
        if (kind === 'emote') b.emote = emote;
        else b.text = text;
        this.live.push(b);
    }
    said(ch, who, text, eid = null) {
        this.bubble(ch, 'say', text);
        this.log({ eid, name: who, text, kind: 'say' });
    }
    chip(ch, who, id, eid = null) {
        const text = chipText(id);
        this.bubble(ch, 'say', text);
        this.log({ eid, name: who, text, kind: 'chip', chip: id });
    }
    emoted(ch, who, name, eid = null) {
        this.bubble(ch, 'emote', null, name);
        this.log({ eid, name: who, text: name, kind: 'emote', emote: name });
    }
    system(text) {
        this.log({ eid: null, name: '', text, kind: 'system' });
    }
    dropCharacter(ch) {
        this.live = this.live.filter((b) => b.ch !== ch);
    }
    /** Per frame: prune and project. `toScreen(wx, wy, out)` maps world px to screen px. */
    update(toScreen) {
        const t = this.now();
        this.live = this.live.filter((b) => t - b.at < (b.kind === 'emote' ? EMOTE_TTL_MS : SAY_TTL_MS));
        if (this.live.length === 0) {
            if (!this.wroteEmpty) this.bridge.bubbles.value = [];
            this.wroteEmpty = true;
            return;
        }
        this.wroteEmpty = false;
        const p = { x: 0, y: 0 };
        this.bridge.bubbles.value = this.live.map((b) => {
            toScreen((b.ch.rx - b.ch.ry) * 32, (b.ch.rx + b.ch.ry) * 16 - HEAD_PX, p);
            const out = { id: b.id, x: p.x, y: p.y, at: b.at, kind: b.kind };
            if (b.kind === 'emote') out.emote = b.emote;
            else out.text = b.text;
            return out;
        });
    }
}
